
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { Classname } from "@rivtn/noseur";
import Btn from "../common/Btn";
import { i18nManager } from "../../i18n";
import { CacheManager, Utils } from "../../../../../utils";
import styles from "../../assets/style.module.css";
import { IHostWebsite, IMember } from "../../../../../services";
import LanguageDropdown from "../../../../../shared/LanguageDropdown";
import { i18nManager as globalI18nManager } from "../../../../../i18n";
import { DASHBOARD_URL, hostLogo, isPageVisible, pagePath, visiblePages } from "../../helpers";

export interface NavbarProps {
    basePath: string;
    cartCount: number;
    profile?: IMember;
    website: IHostWebsite;
    onSignOut: () => void;
}

function Navbar({ website, basePath, profile, cartCount, onSignOut }: NavbarProps) {

    const labels = i18nManager.Labels;
    const authLabels = globalI18nManager.Labels.auth;
    const landingLabels = globalI18nManager.Labels.landing;
    const ddRef = React.useRef<HTMLDivElement>(null);
    const location = ReactRouterDom.useLocation();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [searchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const search = searchParams.toString();
    const logo = hostLogo(website);

    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ddRef.current && !ddRef.current.contains(e.target as Node)) setShowDropdown(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    React.useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    return (<header className={styles["navbar"]}>
        <div className={styles["navbar-inner"]}>
            <ReactRouterDom.NavLink to={{ pathname: `/${basePath}`, search }} className={styles["brand"]}>
                {logo && (<img src={logo} alt={website.host.name} />)}
                <span>{website.host.name}</span>
            </ReactRouterDom.NavLink>

            <nav className={Classname.build(styles["nav-menu"], { [styles["open"]]: mobileOpen })}>
                {visiblePages(website).map(([page, pageSettings]) => (
                    <ReactRouterDom.NavLink end key={page} to={{ pathname: pagePath(basePath, page), search }}
                        className={({ isActive }) => Classname.build(styles["nav-link"], { [styles["active"]]: isActive })}>{pageSettings.label}</ReactRouterDom.NavLink>
                ))}
                <div className={styles["nav-menu-foot"]}><LanguageDropdown /></div>
            </nav>

            <div className={styles["nav-right"]}>
                <span className={styles["nav-desktop"]}><LanguageDropdown /></span>
                {cartCount > 0 && (<ReactRouterDom.NavLink to={{ pathname: pagePath(basePath, "MERCH"), search }} className={styles["nav-icon"]} aria-label="Cart">
                    <i className="fa fa-bag-shopping" /><span className={styles["cart-count"]}>{cartCount}</span>
                </ReactRouterDom.NavLink>)}
                {(website.extra_data?.enable_visitor_account_management) && ((!profile) ? (
                    <button className={styles["nav-icon"]} onClick={() => Utils.signIn()} aria-label={authLabels.sign_in}><i className="fa fa-user" /></button>
                ) : (<div className={styles["dropdown-wrap"]} ref={ddRef}>
                    <img className={styles["avatar"]} onClick={() => setShowDropdown((v) => !v)} src={profile.profile_picture_url} alt={profile.username} />
                    {showDropdown && (<div className={styles["dropdown"]}>
                        <span className={styles["tag"]}>@{profile.username}</span>
                        <a target="_blank" rel="noreferrer" className={styles["dd-item"]} href={DASHBOARD_URL}><i className="fa fa-chart-line" /> {authLabels.dashboard}</a>
                        <a target="_blank" rel="noreferrer" className={styles["dd-item"]} href={`${DASHBOARD_URL}/tournaments?section=REGISTERED`}><i className="fa fa-trophy" /> {landingLabels.my_tournaments}</a>
                        {process.env.REACT_APP_OMNEHOST_SUPPORT_URL && (<a target="_blank" rel="noreferrer" className={styles["dd-item"]} href={process.env.REACT_APP_OMNEHOST_SUPPORT_URL}><i className="fa fa-headset" /> Support</a>)}
                        <hr className={styles["dd-divider"]} />
                        <button className={styles["dd-item"]} onClick={() => {
                            CacheManager.clear();
                            setShowDropdown(false);
                            onSignOut();
                        }}><i className="fa fa-right-from-bracket" /> {authLabels.sign_out}</button>
                    </div>)}
                </div>))}
                {isPageVisible(website, "TOURNAMENTS") && (<span className={styles["nav-desktop"]}>
                    <Btn size="sm" to={{ pathname: pagePath(basePath, "TOURNAMENTS"), search }}>{labels.home.cta}</Btn>
                </span>)}
                <button className={Classname.build(styles["burger"], { [styles["open"]]: mobileOpen })} onClick={() => setMobileOpen((o) => !o)} aria-label={labels.menu} aria-expanded={mobileOpen}>
                    <span /><span />
                </button>
            </div>
        </div>
    </header>);
}

export default Navbar;
